/**
 * useIntakeClassification — Classify device-extracted text via FormSchema catalog (DRR-159)
 *
 * Calls the classifyIntakeText mutation with OCR/transcription text
 * and returns the matched subgraph, entity type, create mutation,
 * confidence score, and extracted field values.
 *
 * FERP Principles:
 * - Repository pattern: all data access via GraphQL mutation
 * - Backend-driven: classification logic lives in IntakeDispatcher (Rust)
 * - Device-native OCR: FERP receives text only, no raw images
 * - Dumb frontend: just displays what backend returns
 */

import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useCallback, useState } from 'react';

// ── GraphQL Operation ────────────────────────────────────────────────────────
// Note: Until codegen regenerates, we define the mutation inline.
// After SDL sync + codegen, import from @/generated/graphql instead.

const CLASSIFY_INTAKE_TEXT = gql`
  mutation ClassifyIntakeText($text: String!) {
    classifyIntakeText(text: $text) {
      subgraph
      entityType
      createMutation
      displayName
      confidence
      extractedFields {
        fieldName
        value
        confidence
      }
    }
  }
`;

// ── Types ────────────────────────────────────────────────────────────────────

export interface ExtractedField {
  fieldName: string;
  value: string;
  confidence: number;
}

export interface IntakeClassification {
  subgraph: string;
  entityType: string;
  createMutation: string;
  displayName: string;
  confidence: number;
  extractedFields: ExtractedField[];
}

export interface UseIntakeClassificationResult {
  /** Classify extracted text against FormSchema catalog */
  classify: (text: string) => Promise<IntakeClassification | null>;
  /** Last classification result */
  classification: IntakeClassification | null;
  /** Whether classification is in progress */
  loading: boolean;
  /** Error from last classification attempt */
  error: Error | null;
  /** Clear the current classification */
  reset: () => void;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useIntakeClassification(): UseIntakeClassificationResult {
  const [classification, setClassification] = useState<IntakeClassification | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const [classifyMutation, { loading }] = useMutation<{
    classifyIntakeText: IntakeClassification | null;
  }>(CLASSIFY_INTAKE_TEXT);

  const classify = useCallback(
    async (text: string): Promise<IntakeClassification | null> => {
      setError(null);
      try {
        const { data } = await classifyMutation({ variables: { text } });
        const result = data?.classifyIntakeText ?? null;
        setClassification(result);
        return result;
      } catch (err) {
        const classifyError = err instanceof Error ? err : new Error('Classification failed');
        setError(classifyError);
        setClassification(null);
        return null;
      }
    },
    [classifyMutation],
  );

  const reset = useCallback(() => {
    setClassification(null);
    setError(null);
  }, []);

  return { classify, classification, loading, error, reset };
}
